import React, {useState} from 'react';

import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import CustomDialog from "../common/CustomDialog";
import SessionInstructionForm from "./SessionInstructionForm";
import { 
  useGetSessionInstructionsQuery,
  useCreateSessionInstructionMutation,
  useUpdateSessionInstructionMutation,
  useRemoveSessionInstructionMutation
} from "../../redux/services/sessionInstructionApi";

const handleCreate = ({post, setNewInstructionDialogOpen}) => async formData => {
  const res = await post(formData);
  if (res?.data) {
    setNewInstructionDialogOpen(false);
  }
  return res;
};

const handleEdit = ({put, setEditId}) => async (id, formData) => {
  const res = await put({id, body: formData});
  if (res?.data) {
    setEditId(null);
  }
  return res;
};

const handleDelete = ({remove}) => async (e, id) => {
  const res = await remove({id});
  return res;
};

const SessionInstructionManager = () => {
  const [newInstructionDialogOpen, setNewInstructionDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const { data, error, isLoading } = useGetSessionInstructionsQuery();
  const [post] = useCreateSessionInstructionMutation();
  const [put] = useUpdateSessionInstructionMutation();
  const [remove] = useRemoveSessionInstructionMutation();
  const deleteHandler = handleDelete({remove});
  const editHandler = handleEdit({put, setEditId});
  const createHandler = handleCreate({post, setNewInstructionDialogOpen});
  const docs = data?.data || [];
  return (
    <Stack>
      <Alert severity='info'>
        Add instructions to send to your clients
        based on the location of the session.
      </Alert>
      <Stack direction='row' spacing={2} alignItems='center' justifyContent='space-between'>
      </Stack>
      {!newInstructionDialogOpen && !editId &&
        <>
          {isLoading && 
            <Stack direction='row' justifyContent='center' alignItems='center'>
              <CircularProgress/>
            </Stack>
          }
          <Stack 
            direction='row' 
            spacing={2} 
            alignItems='center' 
            justifyContent='space-between'
            mt={2}
            >
            <Typography variant='h2' align='center'>
              Your Instructions
            </Typography>
            <Button
              color='success'
              onClick={() => setNewInstructionDialogOpen(true)}
              startIcon={<AddIcon/>}
              >
              New
            </Button>
          </Stack>
          {!isLoading && !docs?.length &&
            <Alert severity='warning'>
              No instructions found. Select "New" to add your first instruction.
            </Alert>
          }
          <List>
            {docs.map((doc) => (
              <ListItem key={doc.id}>
                <ListItemText primary={doc.title} />
                <ListItemIcon>
                  <IconButton 
                    edge="end"
                    aria-label="edit"
                    color='primary'
                    size='small'
                    onClick={e => setEditId(doc.id)}
                  >
                    <EditIcon/>
                  </IconButton>
                </ListItemIcon>
                <ListItemIcon>
                  <IconButton
                    edge="end" 
                    aria-label="delete"
                    color='error'
                    size='small'
                    onClick={e => deleteHandler(e, doc.id)}
                  >
                    <DeleteIcon/>
                  </IconButton>
                </ListItemIcon>
              </ListItem>
            ))}
            </List>
        </>
      }
      {newInstructionDialogOpen && 
        <Stack mt={2}>
          <Typography variant="h2">New Session Instructions</Typography>
          <SessionInstructionForm 
            isEditing={false}
            onSubmit={formData => createHandler(formData)}
          />
        </Stack>
      }
      {!!editId && 
        <Stack mt={2}>
          <Typography variant="h2">Edit Session Instruction</Typography>
          <SessionInstructionForm
            doc={docs.find(doc => doc.id === editId)}
            isEditing={true}
            onSubmit={formData => editHandler(editId, formData)}
          />
        </Stack>
      }
    </Stack>
  );
};

export default SessionInstructionManager;